import React from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Zap, ArrowRight } from 'lucide-react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';

const projects = [
  {
    id: 1,
    image: "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/ko9mw1nc_image.png",
    location: "Tarneit, VIC",
    systemType: "WHES Battery Storage",
    brand: "WHES"
  },
  {
    id: 2,
    image: "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/16bahwen_image.png",
    location: "Hampton Park, VIC",
    systemType: "Deye Battery + Inverter",
    brand: "Deye"
  },
  {
    id: 3,
    image: "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/3auanlb7_image.png",
    location: "Greenvale, VIC",
    systemType: "Sigenergy Battery System",
    brand: "Sigenergy"
  },
  {
    id: 4,
    image: "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/uyof1mwh_image.png",
    location: "Diggers Rest, VIC",
    systemType: "Deye Battery Stack + Inverter",
    brand: "Deye"
  },
  {
    id: 5,
    image: "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/f6rz3ksx_image.png",
    location: "Point Cook, VIC",
    systemType: "Deye Battery System",
    brand: "Deye"
  },
  {
    id: 6,
    image: "https://customer-assets.emergentagent.com/job_powerinnovate/artifacts/sa0h28a8_image.png",
    location: "Wyndham Vale, VIC",
    systemType: "Deye Battery Stack + Inverter",
    brand: "Deye"
  }
];

const ProjectsPage = () => {
  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-[#f8faf5] via-white to-[#f0f7e6] relative overflow-hidden">
          <div className="absolute top-20 right-10 w-72 h-72 bg-[#B5D43B]/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 left-10 w-96 h-96 bg-[#3D5A47]/5 rounded-full blur-3xl" />
          
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="max-w-3xl">
              <span className="text-[#B5D43B] font-semibold text-sm uppercase tracking-wider">Our Portfolio</span>
              <h1 className="heading-1 mt-2 mb-4">
                Recent <span className="text-[#3D5A47]">Projects</span>
              </h1>
              <p className="body-large text-gray-600">
                Explore our completed installations across Victoria. Each project represents our commitment 
                to quality workmanship and complete home electrification solutions.
              </p>
            </div>
          </div>
        </section>

        {/* Projects Grid */}
        <section className="py-16 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {projects.map((project) => (
                <div 
                  key={project.id} 
                  className="group bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-xl transition-all duration-300"
                >
                  {/* Image */}
                  <div className="relative h-64 overflow-hidden">
                    <img 
                      src={project.image} 
                      alt={`Solar installation in ${project.location}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center gap-1.5 text-white">
                        <MapPin className="w-4 h-4" />
                        <span className="font-medium">{project.location}</span>
                      </div>
                    </div>
                  </div>
                  
                  {/* Content */}
                  <div className="p-6">
                    <div className="flex items-start gap-3">
                      <div className="w-12 h-12 rounded-xl bg-[#B5D43B]/15 flex items-center justify-center flex-shrink-0">
                        <Zap className="w-6 h-6 text-[#3D5A47]" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-[#3D5A47] text-lg">{project.systemType}</h3>
                        <p className="text-gray-500">{project.brand}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* More Projects Coming */}
            <div className="mt-12 text-center">
              <div className="inline-block bg-[#f8faf5] rounded-2xl px-8 py-6 border border-[#B5D43B]/20">
                <p className="text-[#3D5A47] font-medium mb-2">More projects coming soon!</p>
                <p className="text-gray-600 text-sm">We're constantly adding new installations to our portfolio.</p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-[#3D5A47]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="heading-2 text-white mb-4">Ready to Start Your Project?</h2>
            <p className="text-white/80 mb-8 max-w-2xl mx-auto">
              Join hundreds of satisfied homeowners who have made the switch to clean energy with Supa Energy Hub.
            </p>
            <Link to="/contact">
              <Button className="bg-[#B5D43B] hover:bg-[#a3c235] text-[#3D5A47] gap-2">
                Get Your Free Quote
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ProjectsPage;
